/**
 * Sound alerts for conference scanning operations.
 * Uses Web Audio API to generate VERY LOUD, distinct tones for each scan result.
 * Maximizes volume using stacked oscillators, high gain, and dynamic compression.
 */

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  return audioContext;
}

/**
 * Creates a maximally loud output chain: gain → compressor → destination.
 * The compressor prevents harsh clipping while keeping the output at maximum loudness.
 */
function createLoudChain(ctx: AudioContext): { input: GainNode; compressor: DynamicsCompressorNode } {
  const gainNode = ctx.createGain();
  const compressor = ctx.createDynamicsCompressor();

  // Aggressive compression to keep everything at max loudness
  compressor.threshold.setValueAtTime(-10, ctx.currentTime);
  compressor.knee.setValueAtTime(0, ctx.currentTime);
  compressor.ratio.setValueAtTime(20, ctx.currentTime);
  compressor.attack.setValueAtTime(0.001, ctx.currentTime);
  compressor.release.setValueAtTime(0.05, ctx.currentTime);

  // Final makeup gain after compression
  const makeupGain = ctx.createGain();
  makeupGain.gain.setValueAtTime(4.0, ctx.currentTime);

  gainNode.connect(compressor);
  compressor.connect(makeupGain);
  makeupGain.connect(ctx.destination);

  return { input: gainNode, compressor };
}

/**
 * Creates a loud oscillator with harmonics for a fuller, louder sound.
 * Stacks the fundamental + octave for maximum perceived loudness.
 */
function createLoudOscillator(
  ctx: AudioContext,
  output: AudioNode,
  freq: number,
  type: OscillatorType,
  startTime: number,
  endTime: number,
  gain: number = 3.0
) {
  // Fundamental frequency
  const osc1 = ctx.createOscillator();
  const g1 = ctx.createGain();
  osc1.type = type;
  osc1.frequency.setValueAtTime(freq, startTime);
  g1.gain.setValueAtTime(gain, startTime);
  g1.gain.exponentialRampToValueAtTime(0.01, endTime);
  osc1.connect(g1);
  g1.connect(output);
  osc1.start(startTime);
  osc1.stop(endTime + 0.01);

  // Octave harmonic for fullness
  const osc2 = ctx.createOscillator();
  const g2 = ctx.createGain();
  osc2.type = type;
  osc2.frequency.setValueAtTime(freq * 2, startTime);
  g2.gain.setValueAtTime(gain * 0.6, startTime);
  g2.gain.exponentialRampToValueAtTime(0.01, endTime);
  osc2.connect(g2);
  g2.connect(output);
  osc2.start(startTime);
  osc2.stop(endTime + 0.01);

  return { osc1, osc2 };
}

/** Success beep: LOUD ascending two-tone (pleasant but unmissable) */
export function playSuccessSound() {
  const ctx = getAudioContext();
  const { input } = createLoudChain(ctx);
  const now = ctx.currentTime;

  createLoudOscillator(ctx, input, 880, 'sine', now, now + 0.15, 3.0);
  createLoudOscillator(ctx, input, 1320, 'sine', now + 0.15, now + 0.35, 3.0);
}

/** Duplicate alert: LOUD rapid repeating warning beeps */
export function playDuplicateSound() {
  const ctx = getAudioContext();
  const { input } = createLoudChain(ctx);
  const now = ctx.currentTime;

  createLoudOscillator(ctx, input, 600, 'square', now, now + 0.15, 3.5);
  createLoudOscillator(ctx, input, 600, 'square', now + 0.22, now + 0.37, 3.5);
  createLoudOscillator(ctx, input, 600, 'square', now + 0.44, now + 0.59, 3.5);
}

/** Divergence alert: LOUD descending alarm siren (urgent, impossible to ignore) */
export function playDivergenceSound() {
  const ctx = getAudioContext();
  const { input } = createLoudChain(ctx);
  const now = ctx.currentTime;

  // First sweep
  const osc1 = ctx.createOscillator();
  const g1 = ctx.createGain();
  osc1.type = 'sawtooth';
  osc1.frequency.setValueAtTime(900, now);
  osc1.frequency.linearRampToValueAtTime(300, now + 0.4);
  g1.gain.setValueAtTime(4.0, now);
  g1.gain.exponentialRampToValueAtTime(0.01, now + 0.45);
  osc1.connect(g1);
  g1.connect(input);
  osc1.start(now);
  osc1.stop(now + 0.46);

  // Harmonic for first sweep
  const osc1h = ctx.createOscillator();
  const g1h = ctx.createGain();
  osc1h.type = 'sawtooth';
  osc1h.frequency.setValueAtTime(1800, now);
  osc1h.frequency.linearRampToValueAtTime(600, now + 0.4);
  g1h.gain.setValueAtTime(2.5, now);
  g1h.gain.exponentialRampToValueAtTime(0.01, now + 0.45);
  osc1h.connect(g1h);
  g1h.connect(input);
  osc1h.start(now);
  osc1h.stop(now + 0.46);

  // Second sweep
  const osc2 = ctx.createOscillator();
  const g2 = ctx.createGain();
  osc2.type = 'sawtooth';
  osc2.frequency.setValueAtTime(900, now + 0.5);
  osc2.frequency.linearRampToValueAtTime(300, now + 0.9);
  g2.gain.setValueAtTime(4.0, now + 0.5);
  g2.gain.exponentialRampToValueAtTime(0.01, now + 0.95);
  osc2.connect(g2);
  g2.connect(input);
  osc2.start(now + 0.5);
  osc2.stop(now + 0.96);

  // Harmonic for second sweep
  const osc2h = ctx.createOscillator();
  const g2h = ctx.createGain();
  osc2h.type = 'sawtooth';
  osc2h.frequency.setValueAtTime(1800, now + 0.5);
  osc2h.frequency.linearRampToValueAtTime(600, now + 0.9);
  g2h.gain.setValueAtTime(2.5, now + 0.5);
  g2h.gain.exponentialRampToValueAtTime(0.01, now + 0.95);
  osc2h.connect(g2h);
  g2h.connect(input);
  osc2h.start(now + 0.5);
  osc2h.stop(now + 0.96);
}

/** Error alert: LOUD low buzz (unmistakable failure sound) */
export function playErrorSound() {
  const ctx = getAudioContext();
  const { input } = createLoudChain(ctx);
  const now = ctx.currentTime;

  createLoudOscillator(ctx, input, 200, 'square', now, now + 0.35, 3.5);
  createLoudOscillator(ctx, input, 150, 'square', now + 0.4, now + 0.75, 3.5);
}

/** Play the appropriate sound based on scan status */
export function playScanSound(status: string) {
  switch (status) {
    case 'sucesso':
    case 'conferida':
      playSuccessSound();
      break;
    case 'duplicado':
      playDuplicateSound();
      break;
    case 'divergencia':
      playDivergenceSound();
      break;
    case 'erro':
    case 'erro_parse':
      playErrorSound();
      break;
  }
}
